import {
  DEFAULT_LOCALE,
  I18N_NAMESPACES,
  isLocale,
  type Locale,
  negotiateLocale,
} from '@bb/shared';
import i18next from 'i18next';
import ICU from 'i18next-icu';
import { initReactI18next } from 'react-i18next';

// Catalogs are bundled per (locale, namespace) and code-split so a Booking-only
// user never downloads Invoicing strings.
const catalogs = import.meta.glob<{ default: Record<string, unknown> }>(
  '../../../../packages/shared/locales/*/*.json',
);

async function loadNamespace(locale: Locale, ns: string): Promise<Record<string, unknown>> {
  const key = `../../../../packages/shared/locales/${locale}/${ns}.json`;
  const loader = catalogs[key];
  if (!loader) return {};
  return (await loader()).default;
}

const STORAGE_KEY = 'bb.locale';

export function readStoredLocale(): Locale | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return isLocale(value) ? value : null;
  } catch {
    return null;
  }
}

export function detectInitialLocale(): Locale {
  const fromQuery = new URLSearchParams(window.location.search).get('lang');
  if (isLocale(fromQuery)) return fromQuery;
  return readStoredLocale() ?? negotiateLocale(navigator.languages);
}

export async function setupI18n(): Promise<typeof i18next> {
  const locale = detectInitialLocale();
  await i18next
    .use(ICU)
    .use(initReactI18next)
    .init({
      lng: locale,
      fallbackLng: DEFAULT_LOCALE,
      supportedLngs: ['en', 'ja'],
      ns: ['common', 'errors'],
      defaultNS: 'common',
      resources: {},
      interpolation: { escapeValue: false },
      react: { useSuspense: false },
    });
  for (const ns of ['common', 'errors']) {
    i18next.addResourceBundle(locale, ns, await loadNamespace(locale, ns), true, true);
  }
  document.documentElement.lang = locale;
  return i18next;
}

/** Load a namespace on demand (e.g. when entering a product). Idempotent. */
export async function ensureNamespace(ns: (typeof I18N_NAMESPACES)[number]): Promise<void> {
  const locale = i18next.language as Locale;
  if (i18next.hasResourceBundle(locale, ns)) return;
  i18next.addResourceBundle(locale, ns, await loadNamespace(locale, ns), true, true);
}

export async function changeLocale(locale: Locale): Promise<void> {
  for (const ns of I18N_NAMESPACES) {
    if (i18next.hasResourceBundle(i18next.language, ns) && !i18next.hasResourceBundle(locale, ns)) {
      i18next.addResourceBundle(locale, ns, await loadNamespace(locale, ns), true, true);
    }
  }
  await i18next.changeLanguage(locale);
  document.documentElement.lang = locale;
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* private mode */
  }
}
